import React, { useEffect, useRef } from 'react';
import {
    Redirect,
    Switch,
    useLocation,
    useHistory,
    useRouteMatch,
} from 'react-router-dom';
import { useAuth0, useAuth0Data } from 'neon-auth0';
import { Box, CircularProgress } from '@mui/material';
import packageJson from '../package.json';
import {
    getPlatformInfo,
    useLogger,
    sessionId,
} from './components/providers/logger/Logger';
import {
    LogAction,
    LogLevel,
} from './components/providers/logger/logger-types';
import { useUser } from './components/providers/UserProvider';
import { ResearchRole } from './shared/global-enums';
import PublisherContent from './components/containers/admin/PublisherContent';
import AuthenticatedRoute from './components/ui/AuthenticatedRoute';
import { useMutation, useQuery } from '@apollo/react-hooks';
import ResearchAppBar from './components/ui/ResearchAppBar';
import ClientContainer from './components/containers/client/ClientContainer';
import { useUpBreakpoints } from './components/providers/BreakpointsProvider';
import { clientRoutes, fullScreenRoutes, Route as AppRoute } from './routes';
import gql from 'graphql-tag';
import { redirectToCompleteProfile } from 'neon-utils';
import { config } from './config';
import { redirectToUrl } from './index';
import { UPDATE_USER_FROM_TOKEN } from './gql/users';
import { IsUserProfileComplete, User } from './shared/types';
import { ViewTrackerProvider } from './components/providers/ViewTrackerProvider';
import jwt from 'jwt-decode';
import * as appcues from 'neon-appcues';
import { baseUrls, getUrlsRec } from './utils/baseUrls.utils';
import { USER_GUIDE_PERMITTED_ROLES } from './utils/user-guide-access.utils';
import { VersionChecker } from './components/VersionChecker';
import { ScrollableBodyLayout } from '@mds/app-layout';

const { version } = packageJson;

const isRouteOrChildRoute = (pathname: string, route: string): boolean =>
    pathname === route || pathname.startsWith(`${route}/`);

const isChannelsNavigationActive = (pathname: string): boolean =>
    [baseUrls.channels.main, baseUrls.browse.main, '/subscribe/channel'].some(
        (route) => isRouteOrChildRoute(pathname, route)
    );

const isSubscriptionsNavigationActive = (pathname: string): boolean =>
    [baseUrls.subscriptions, baseUrls.emailNotifications].some((route) =>
        isRouteOrChildRoute(pathname, route)
    );

const isUserGuideNavigationActive = (pathname: string): boolean =>
    isRouteOrChildRoute(pathname, baseUrls.userGuides);

const isPublisherNavigationActive = (pathname: string): boolean =>
    isRouteOrChildRoute(pathname, baseUrls.publisher.main);

export const IS_USER_PROFILE_COMPLETE = gql`
    query isUserProfileComplete {
        document: isUserProfileComplete {
            isProfileComplete
        }
    }
`;

const HAS_USER_GUIDE_ARTICLES = gql`
    query hasUserGuideArticles {
        getArticles(params: { isUserGuideOnly: true }, first: 1, page: 0) {
            totalCount
        }
    }
`;

const App: React.FC = (): JSX.Element | null => {
    const { logout, isAuthenticated, getAccessTokenSilently } = useAuth0();
    const { idToken, accessToken } = useAuth0Data();
    const location = useLocation();
    const { updateIdentity, identity, printLog } = useLogger();
    const { hasScope, userDetails } = useUser();

    const { loading: isProfileCompletedLoading } = useQuery<{
        document: IsUserProfileComplete;
    }>(IS_USER_PROFILE_COMPLETE, {
        fetchPolicy: 'network-only',
        onCompleted(response) {
            if (!response.document.isProfileComplete) {
                redirectToCompleteProfile(
                    config.landingPageUrl,
                    window.location.href
                );
            }
        },
        onError(error) {
            printLog(
                LogLevel.ERROR,
                LogAction.Authentication,
                'Unable to check Insights profile completion',
                {
                    message: error.message,
                }
            );
        },
    });
    const { data: userGuideArticlesData } = useQuery<{
        getArticles: { totalCount: number };
    }>(HAS_USER_GUIDE_ARTICLES, {
        fetchPolicy: 'network-only',
    });
    const history = useHistory();
    const { md } = useUpBreakpoints();

    const [updateUserFromToken] = useMutation(UPDATE_USER_FROM_TOKEN);
    const fullScreenRouteMatch = useRouteMatch(
        fullScreenRoutes.map((r) => r.path)
    );
    const allRoutes = getUrlsRec(baseUrls);
    const hasRouteMatch = useRouteMatch(allRoutes);
    useEffect(() => {
        const redirectUrls = [
            baseUrls.browse.main,
            `${baseUrls.browse.main}/`,
            baseUrls.topics,
            `${baseUrls.topics}/`,
        ];
        if (redirectUrls.indexOf(location.pathname) > -1) {
            history.push(baseUrls.channels.main);
        }
    }, [location, history]);

    useEffect(() => {
        if (!hasRouteMatch) {
            history.push(baseUrls.forYou);
        }
    }, [hasRouteMatch, history]);

    const menuItems = [];

    if (USER_GUIDE_PERMITTED_ROLES.some(hasScope)) {
        const hasUserGuideArticles =
            (userGuideArticlesData?.getArticles?.totalCount || 0) > 0;

        menuItems.push({ name: 'For you', link: baseUrls.forYou });
        menuItems.push({
            name: 'Channels',
            link: baseUrls.channels.main,
            isActive: isChannelsNavigationActive(location.pathname),
        });
        menuItems.push({
            name: 'Subscriptions',
            link: baseUrls.subscriptions,
            isActive: isSubscriptionsNavigationActive(location.pathname),
        });
        if (hasUserGuideArticles) {
            menuItems.push({
                name: 'User Guides',
                link: baseUrls.userGuides,
                isActive: isUserGuideNavigationActive(location.pathname),
            });
        }
    }

    if (
        md &&
        [
            ResearchRole.Administrator,
            ResearchRole.Contributor,
            ResearchRole.Editor,
        ].some(hasScope)
    ) {
        menuItems.push({
            name: "Publisher's Dashboard",
            link: baseUrls.publisher.main,
            isActive: isPublisherNavigationActive(location.pathname),
        });
    }

    useEffect(() => {
        const neonUserId =
            jwt<any>(accessToken)[config.auth0.namespace + 'neonUserId'];
        updateIdentity(neonUserId);
    }, [accessToken, updateIdentity]);

    useEffect(() => {
        if (!identity) return;
        const platformInfo = getPlatformInfo(version);
        printLog(
            LogLevel.INFO,
            LogAction.LoggedIn,
            'Logged in to NeonResearch',
            platformInfo
        );
        // eslint-disable-next-line
    }, [identity]);

    const hasUpdateUserRun = useRef(false);
    useEffect(() => {
        const token = jwt<any>(accessToken);
        const hasIntelligencePermission =
            token.scope.split(' ').indexOf('intelligence') > -1;

        const setupAppcues = (user?: User) => {
            if (['development'].includes(config.nodeEnv)) {
                return;
            }

            appcues.init({
                appName: 'insights',
                accessToken: accessToken,
                getAccessToken: getAccessTokenSilently,
                idToken: idToken.__raw,
                log(message, data) {
                    printLog(LogLevel.INFO, LogAction.Appcues, message, data);
                },
                analyticOptions: {
                    env: appcues.environmentResolver(config.nodeEnv),
                    recordAnalytics: true,
                    analyticsOnly: false,
                    sessionData: {
                        sessionId,
                        appName: 'Neon Insights',
                        appVersion: version,
                    },
                },
            });

            const rootCategories =
                user?.subscriptions?.categories.map(
                    (subscription) => subscription?.category?.rootCategory?.name
                ) || [];

            appcues.identify({
                insightsRole: user?.role,
                hasAgsSubscription: rootCategories.some(
                    (name) => name === 'Agriculture'
                ),
                hasEnergySubscription: rootCategories.some(
                    (name) => name === 'Energy'
                ),
                hasMetalsSubscription: rootCategories.some(
                    (name) => name === 'Metals'
                ),
                hasIntelligencePermission,
                subscribedToPremiumChannel: user?.channelSubscriptions.some(
                    (cs) => cs.subscriptionState === 'active'
                ),
                ...user?.channelSubscriptions.reduce(
                    (map, cs) => ({
                        ...map,
                        [`subscribedToChannel-${cs.channelId}-${cs.channel?.name}`]:
                            cs.subscriptionState === 'active',
                    }),
                    {}
                ),
            });

            appcues.analyticsLog({});
            history.listen(appcues.page);
            history.listen((_, __) => appcues.analyticsLog({}));
        };

        if (isAuthenticated && idToken && !hasUpdateUserRun.current) {
            hasUpdateUserRun.current = true;
            updateUserFromToken({ variables: { idToken: idToken.__raw } })
                .then((x) => {
                    setupAppcues(x.data?.updateUserFromToken?.user);
                    if (
                        x?.data?.updateUserFromToken?.isNewUser &&
                        !window.location.href.includes('browse/article') &&
                        !x?.data?.user?.channelSubscriptions?.length
                    ) {
                        history.push(baseUrls.forYou);
                    }
                })
                .catch((error) => {
                    printLog(
                        LogLevel.ERROR,
                        LogAction.Authentication,
                        'Unable to update Insights user from token',
                        {
                            message: error?.message,
                        }
                    );
                });
        }
    }, [
        isAuthenticated,
        idToken,
        accessToken,
        updateUserFromToken,
        history,
        getAccessTokenSilently,
        printLog,
    ]);

    if (isProfileCompletedLoading) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (fullScreenRouteMatch) {
        return (
            <ViewTrackerProvider>
                <Switch>
                    {fullScreenRoutes.map((r: AppRoute<string>, i: number) => (
                        <AuthenticatedRoute
                            exact
                            key={i + location.pathname}
                            {...r}
                        />
                    ))}
                </Switch>
            </ViewTrackerProvider>
        );
    }

    return (
        <ScrollableBodyLayout
            disableGutter
            appBar={
                isAuthenticated && (
                    <ResearchAppBar
                        userDetails={userDetails}
                        menuItems={menuItems}
                        accessToken={accessToken}
                        onSignOut={(): void => {
                            logout({ returnTo: redirectToUrl()?.href });
                            printLog(
                                LogLevel.INFO,
                                LogAction.LoggedOut,
                                'Logged out'
                            );
                        }}
                    />
                )
            }
            slotProps={{
                content: {
                    sx: {
                        minWidth: 0,
                    },
                },
            }}
        >
            <VersionChecker />
            <Switch>
                <Redirect
                    exact
                    from="/"
                    to={
                        md &&
                        [
                            ResearchRole.Administrator,
                            ResearchRole.Contributor,
                            ResearchRole.Editor,
                        ].some(hasScope)
                            ? baseUrls.publisher.main
                            : baseUrls.forYou
                    }
                />
                <AuthenticatedRoute
                    path={baseUrls.publisher.main}
                    component={PublisherContent}
                    permittedRoles={[
                        ResearchRole.Administrator,
                        ResearchRole.Editor,
                        ResearchRole.Contributor,
                    ]}
                />
                <ClientContainer>
                    <Switch>
                        {clientRoutes.map((r: AppRoute<string>, i: number) => (
                            <AuthenticatedRoute
                                exact
                                key={i + location.pathname}
                                {...r}
                            />
                        ))}
                    </Switch>
                </ClientContainer>
            </Switch>
        </ScrollableBodyLayout>
    );
};

export default App;
